/**
 * MOTOR ELÉCTRICO — cargas, demanda, balanceo, conductor y canalización.
 * =============================================================================
 *
 * Es el módulo más valioso del negocio: el 100% del ingreso previsto hasta octubre, y
 * el que ningún competidor puede copiar sin comprar instrumentos y aprender a usarlos.
 *
 * Y es donde el Principio 7 se pone a prueba de verdad: **quien usa esto no sabe
 * dimensionar un conductor.** Su trabajo es contar aparatos y medir. Traducir de lo
 * observable a lo técnico es trabajo de este archivo.
 *
 * ---------------------------------------------------------------------------
 * PROCEDENCIA DE CADA TABLA — leerlo antes de confiar en un número
 *
 * Las tablas normativas se investigaron el 2026-08-14 y **cada una lleva su fuente en
 * el comentario que la precede**:
 *
 *   `FUENTE:` texto primario   leído del DOF, con su sección y su `codigo`.
 *   `FUENTE:`                  leído de un documento técnico que cita la norma, y
 *                              contrastado contra una segunda fuente independiente.
 *
 * **Ya no queda ningún `CONFIRMAR:` en este archivo.** El artículo 220 —cargas por metro
 * cuadrado, factores de demanda y método opcional de vivienda— sí está publicado en el
 * DOF, en la PRIMERA sección, la misma del índice y los artículos 100 y 110. Lo que había
 * fallado antes era la extracción y no la búsqueda: la página pesa 1.7 MB y el artículo
 * queda pasada la mitad, así que las herramientas que resumen la devolvían truncada. Se
 * resolvió bajándola con `curl --ssl-no-revoke` —el certificado del DOF tumba a curl por
 * defecto— y buscando en el HTML crudo. Queda escrito por si hay que volver.
 *
 * La norma vigente es **NOM-001-SEDE-2012** (DOF 29/11/2012). No existe una versión
 * 2022 publicada: está verificado en `areas/Plan_de_Arranque/Marco_Legal_Recableado_
 * Electrico.md` §6, contra el DOF.
 *
 * ---------------------------------------------------------------------------
 * LO QUE ESTE ARCHIVO NO HACE, Y NO DEBE HACER
 *
 * · **No emite dictamen de conformidad.** Eso lo hace una Unidad de Verificación
 *   acreditada. Decisión cerrada en el dictamen legal.
 * · **No calcula cortocircuito ni coordinación de protecciones.** Es ingeniería de otro
 *   nivel y no se necesita en vivienda unifamiliar. Prometerlo sería inflar el alcance.
 * · **No sustituye a una persona calificada.** La norma define «persona calificada» en
 *   su art. 100 y exige que la construcción la ejecute una. Esto dimensiona y cotiza;
 *   la obra la hace la cuadrilla y la responde Juan.
 * ============================================================================= */

/* La costura con el catálogo de Juan: el motor elige por norma y cobra con lo que él
   tenga dado de alta. Ver la cabecera de `catalogo-tecnico.js`. */
import { resolver, resolverProteccion, renglon } from './catalogo-tecnico.js'

/* ============================================================================
   1. CONDUCTORES
   ========================================================================== */

/**
 * Tabla 310-15(b)(16) — ampacidades permisibles en conductores aislados de COBRE,
 * hasta 2000 V, no más de tres conductores portadores de corriente en una
 * canalización, a temperatura ambiente de 30 °C.
 *
 * `FUENTE:` documento técnico de Latincasa que reproduce la tabla citando la
 * NOM-001-SEDE-2012, contrastado contra calcele.com. Las dos fuentes coinciden de
 * 8 AWG hacia arriba. Difieren en 14, 12 y 10 porque una publica la ampacidad de la
 * tabla y la otra ya publica el tope de protección — ver `LIMITE_PROTECCION`.
 *
 * `mm2` es la sección nominal del conductor, de la misma tabla.
 */
export const CONDUCTORES = [
  { awg: '14',  mm2: 2.08,  a60: 15,  a75: 20,  a90: 25  },
  { awg: '12',  mm2: 3.31,  a60: 20,  a75: 25,  a90: 30  },
  { awg: '10',  mm2: 5.26,  a60: 30,  a75: 35,  a90: 40  },
  { awg: '8',   mm2: 8.37,  a60: 40,  a75: 50,  a90: 55  },
  { awg: '6',   mm2: 13.3,  a60: 55,  a75: 65,  a90: 75  },
  { awg: '4',   mm2: 21.2,  a60: 70,  a75: 85,  a90: 95  },
  { awg: '3',   mm2: 26.7,  a60: 85,  a75: 100, a90: 115 },
  { awg: '2',   mm2: 33.6,  a60: 95,  a75: 115, a90: 130 },
  { awg: '1',   mm2: 42.4,  a60: 110, a75: 130, a90: 145 },
  { awg: '1/0', mm2: 53.49, a60: 125, a75: 150, a90: 170 },
  { awg: '2/0', mm2: 67.43, a60: 145, a75: 175, a90: 195 },
  { awg: '3/0', mm2: 85.01, a60: 165, a75: 200, a90: 225 },
  { awg: '4/0', mm2: 107.2, a60: 195, a75: 230, a90: 260 }
]

/**
 * Art. 240-4(d) — **tope de protección contra sobrecorriente de los calibres chicos.**
 *
 * Esta es la regla que más se ignora y la que más se nota: aunque la columna de 90 °C
 * diga que un 14 AWG lleva 25 A, **la protección no puede pasar de 15 A**. Poner un
 * interruptor de 20 A sobre calibre 14 es exactamente el error que deja un conductor
 * calentándose sin que nada dispare.
 *
 * `FUENTE:` nota al pie de la Tabla 310-15(b)(16) —«Véase 240-4(d) para limitaciones de
 * protección contra sobrecorriente del conductor»— y los valores publicados por
 * calcele.com como ampacidad efectiva de esos tres calibres.
 */
export const LIMITE_PROTECCION = { '14': 15, '12': 20, '10': 30 }

/**
 * Tabla 310-15(b)(2)(a) — factores de corrección por temperatura ambiente distinta
 * de 30 °C. `FUENTE:` el mismo documento de Latincasa.
 *
 * Importa más de lo que parece en esta zona: un tramo por plafón o por azotea en
 * Ciudad Satélite pasa de 40 °C sin problema, y ahí un 12 AWG pierde casi un 20% de
 * su capacidad.
 */
export const CORRECCION_TEMPERATURA = [
  { hasta: 10, f60: 1.29, f75: 1.20, f90: 1.15 },
  { hasta: 15, f60: 1.22, f75: 1.15, f90: 1.12 },
  { hasta: 20, f60: 1.15, f75: 1.11, f90: 1.08 },
  { hasta: 25, f60: 1.08, f75: 1.05, f90: 1.04 },
  { hasta: 30, f60: 1.00, f75: 1.00, f90: 1.00 },
  { hasta: 35, f60: 0.91, f75: 0.94, f90: 0.96 },
  { hasta: 40, f60: 0.82, f75: 0.88, f90: 0.91 },
  { hasta: 45, f60: 0.71, f75: 0.82, f90: 0.87 },
  { hasta: 50, f60: 0.58, f75: 0.75, f90: 0.82 },
  { hasta: 55, f60: 0.41, f75: 0.67, f90: 0.76 },
  { hasta: 60, f60: null, f75: 0.58, f90: 0.71 }
]

/**
 * Factores de ajuste por más de tres conductores portadores de corriente en la misma
 * canalización. `FUENTE:` el mismo documento, «Tabla 3».
 *
 * Se aplica cuando se meten varios circuitos por el mismo tubo, que es lo normal en un
 * recableado: seis conductores en un tubo bajan la capacidad de cada uno al 80%.
 */
export const AJUSTE_AGRUPAMIENTO = [
  { hasta: 3, f: 1.00 },
  { hasta: 6, f: 0.80 },
  { hasta: 9, f: 0.70 },
  { hasta: 20, f: 0.50 },
  { hasta: 30, f: 0.45 },
  { hasta: 40, f: 0.40 },
  { hasta: Infinity, f: 0.35 }
]

/**
 * Tabla 9 — impedancia eficaz a factor de potencia 0.85, en ohm por kilómetro, para
 * tres conductores individuales de cobre en tubo conduit de PVC, 600 V, 60 Hz, 75 °C.
 *
 * `FUENTE:` el mismo documento de Latincasa, «Tabla 9».
 *
 * **Por qué se usa esto y no la resistividad del cobre.** El método simplificado
 * `ΔV = 2·L·I·ρ/S` ignora la reactancia inductiva y la resistencia en alterna a
 * temperatura de operación. En calibres chicos la diferencia es pequeña; a partir de
 * 2 AWG la reactancia empieza a pesar. Esta tabla es la que usa la norma.
 */
export const IMPEDANCIA = {
  '14': 8.9, '12': 5.6, '10': 3.6, '8': 2.26, '6': 1.44, '4': 0.95,
  '3': 0.75, '2': 0.62, '1': 0.52, '1/0': 0.43, '2/0': 0.36,
  '3/0': 0.289, '4/0': 0.243
}

/**
 * Art. 110-14 — **qué columna de temperatura se puede usar.**
 *
 * `FUENTE:` nota del documento de Latincasa citando el artículo: si la corriente del
 * circuito es menor de 100 A se elige la capacidad a **60 °C**; si es mayor de 100 A,
 * a **75 °C**. El límite no lo pone el cable sino la terminal del equipo donde se
 * conecta, y por eso un cable de 90 °C no autoriza a usar su columna de 90 °C.
 *
 * En una casa, prácticamente todos los circuitos derivados caen en la columna de 60 °C.
 * **Usar la de 90 °C —que es la que "da mejor número"— es el error clásico.**
 */
export const columnaPorCorriente = corriente => (corriente > 100 ? 'a75' : 'a60')

/** Factor de corrección por temperatura ambiente, para la columna elegida. */
export function factorTemperatura (ambienteC, columna) {
  const fila = CORRECCION_TEMPERATURA.find(f => ambienteC <= f.hasta)
    || CORRECCION_TEMPERATURA[CORRECCION_TEMPERATURA.length - 1]
  return fila['f' + columna.slice(1)] ?? 0
}

/** Factor de ajuste por número de conductores portadores en la canalización. */
export const factorAgrupamiento = n =>
  (AJUSTE_AGRUPAMIENTO.find(a => n <= a.hasta) || { f: 0.35 }).f

/**
 * Caída de tensión en por ciento, monofásico.
 *
 *   ΔV = 2 · I · L · Z          (ida y vuelta: la tabla da ohms AL NEUTRO por km)
 *   %  = ΔV / V₀ · 100
 *
 * @param corriente A
 * @param metros longitud de UN sentido del circuito, m
 * @param awg calibre
 * @param tension tensión de fase a neutro, V (127 en México)
 */
export function caidaTension (corriente, metros, awg, tension = 127) {
  const z = IMPEDANCIA[awg]
  if (!z) return null
  const km = metros / 1000
  const delta = 2 * corriente * km * z
  return { volts: delta, porciento: (delta / tension) * 100 }
}

/**
 * Límite de caída de tensión que se adopta como CRITERIO DE DISEÑO.
 *
 * `CONFIRMAR:` el 3% en circuito derivado y 5% acumulado es el criterio de diseño de
 * uso universal, y en la NOM aparece como **nota informativa, no como obligación**.
 * Se usa como compromiso propio de calidad — que es más exigente que la norma— pero
 * **nunca debe presentarse al cliente como requisito legal.** Es justo el matiz que un
 * cliente técnico verifica, y decirlo mal cuesta credibilidad.
 */
export const LIMITE_CAIDA = { derivado: 3, total: 5, esCriterioPropio: true }

/**
 * Selecciona el calibre de un circuito. **Manda el criterio más exigente de los dos.**
 *
 * 1. **Ampacidad**, con la columna que permite el art. 110-14, corregida por
 *    temperatura ambiente y por agrupamiento.
 * 2. **Caída de tensión** bajo el límite adoptado.
 *
 * Y encima, el tope de protección del art. 240-4(d) para los calibres chicos.
 *
 * Devuelve el calibre y **por qué criterio quedó ese y no uno más chico** — que es lo
 * que hace auditable la decisión (Principio 9).
 */
export function calibrePara ({
  corriente, metros, tension = 127, ambienteC = 30, conductoresEnTubo = 3
}) {
  const columna = columnaPorCorriente(corriente)
  const fT = factorTemperatura(ambienteC, columna)
  const fA = factorAgrupamiento(conductoresEnTubo)

  let porAmpacidad = null
  for (const c of CONDUCTORES) {
    const capacidad = c[columna] * fT * fA
    // El tope de protección no cambia la ampacidad del cable, pero sí el interruptor
    // que se le puede poner, y por lo tanto el circuito que puede alimentar.
    const tope = LIMITE_PROTECCION[c.awg]
    const util = tope ? Math.min(capacidad, tope) : capacidad
    if (util >= corriente) { porAmpacidad = { ...c, capacidad, util }; break }
  }

  let porCaida = null
  for (const c of CONDUCTORES) {
    const cv = caidaTension(corriente, metros, c.awg, tension)
    if (cv && cv.porciento <= LIMITE_CAIDA.derivado) { porCaida = { ...c, caida: cv }; break }
  }

  if (!porAmpacidad && !porCaida) return null

  const iA = porAmpacidad ? CONDUCTORES.indexOf(CONDUCTORES.find(c => c.awg === porAmpacidad.awg)) : -1
  const iC = porCaida ? CONDUCTORES.indexOf(CONDUCTORES.find(c => c.awg === porCaida.awg)) : -1
  const gana = iC > iA ? 'caida' : 'ampacidad'
  const elegido = gana === 'caida' ? porCaida : porAmpacidad

  const cvFinal = caidaTension(corriente, metros, elegido.awg, tension)

  return {
    awg: elegido.awg,
    mm2: elegido.mm2,
    manda: gana,
    caida: cvFinal,
    porAmpacidad: porAmpacidad?.awg ?? null,
    porCaida: porCaida?.awg ?? null,
    columna,
    factorTemperatura: fT,
    factorAgrupamiento: fA,
    proteccion: proteccionPara(corriente, elegido.awg),
    // El texto que explica la decisión, ya redactado: el operador no lo tiene que armar.
    porque: gana === 'caida'
      ? `Por ampacidad bastaría calibre ${porAmpacidad?.awg ?? '—'}, pero a ${metros} m ` +
        `la caída de tensión se pasaría del ${LIMITE_CAIDA.derivado}%. Sube a ${elegido.awg}.`
      : `Lo manda la ampacidad: ${corriente} A con la columna de ${columna === 'a60' ? '60' : '75'} °C` +
        (fT !== 1 ? `, corregida por ${ambienteC} °C de ambiente` : '') +
        (fA !== 1 ? ` y por ${conductoresEnTubo} conductores en el tubo` : '') + '.'
  }
}

/** Interruptor comercial inmediato superior, con el tope del art. 240-4(d). */
export const PASOS_INTERRUPTOR = [15, 20, 30, 40, 50, 60, 70, 80, 100, 125, 150, 175, 200]

export function proteccionPara (corriente, awg) {
  const paso = PASOS_INTERRUPTOR.find(p => p >= corriente) || 200
  const tope = LIMITE_PROTECCION[awg]
  return tope ? Math.min(paso, tope) : paso
}

/* ============================================================================
   2. CARGAS — lo observable, traducido
   ========================================================================== */

/**
 * Catálogo de aparatos con su potencia típica.
 *
 * **Este catálogo es el Principio 7 hecho datos.** El operador no sabe cuántos watts
 * consume una secadora; sabe que hay una secadora. Cuenta y elige; el sistema traduce.
 *
 * `CONFIRMAR:` las potencias son valores típicos de placa para aparato residencial
 * mexicano, **no fichas de un modelo concreto**. Sirven para dimensionar y quedan
 * marcadas como no calibradas; cuando el cliente tenga un aparato a la vista, se lee
 * su placa y se corrige. Un dato de placa siempre gana a esta tabla.
 *
 * `dedicado: true` marca las cargas que exigen circuito propio.
 * `fp` es el factor de potencia, donde difiere de 1.
 */
export const APARATOS = [
  // --- Cocina ---
  { id: 'refri', nombre: 'Refrigerador', watts: 350, zona: 'Cocina', dedicado: true, sinVerificar: true },
  { id: 'micro', nombre: 'Horno de microondas', watts: 1200, zona: 'Cocina', dedicado: true, sinVerificar: true },
  { id: 'induccion', nombre: 'Parrilla de inducción', watts: 3500, zona: 'Cocina', dedicado: true, v: 220, sinVerificar: true },
  { id: 'horno-el', nombre: 'Horno eléctrico', watts: 2500, zona: 'Cocina', dedicado: true, v: 220, sinVerificar: true },
  { id: 'lavavajillas', nombre: 'Lavavajillas', watts: 1500, zona: 'Cocina', dedicado: true, sinVerificar: true },
  { id: 'campana', nombre: 'Campana extractora', watts: 200, zona: 'Cocina', sinVerificar: true },
  { id: 'licuadora', nombre: 'Licuadora y pequeños', watts: 600, zona: 'Cocina', sinVerificar: true },

  // --- Lavandería ---
  { id: 'lavadora', nombre: 'Lavadora', watts: 800, zona: 'Lavandería', dedicado: true, sinVerificar: true },
  { id: 'secadora', nombre: 'Secadora eléctrica', watts: 5000, zona: 'Lavandería', dedicado: true, v: 220, sinVerificar: true },
  { id: 'plancha', nombre: 'Plancha', watts: 1200, zona: 'Lavandería', sinVerificar: true },

  // --- Clima ---
  { id: 'mini1', nombre: 'Minisplit 1 tonelada', watts: 1200, zona: 'Clima', dedicado: true, fp: 0.9, sinVerificar: true },
  { id: 'mini15', nombre: 'Minisplit 1.5 toneladas', watts: 1800, zona: 'Clima', dedicado: true, fp: 0.9, sinVerificar: true },
  { id: 'mini2', nombre: 'Minisplit 2 toneladas', watts: 2400, zona: 'Clima', dedicado: true, v: 220, fp: 0.9, sinVerificar: true },
  { id: 'calefactor', nombre: 'Calefactor eléctrico', watts: 1500, zona: 'Clima', sinVerificar: true },

  // --- Agua ---
  { id: 'boiler-el', nombre: 'Calentador eléctrico de paso', watts: 6000, zona: 'Agua', dedicado: true, v: 220, sinVerificar: true },
  { id: 'bomba', nombre: 'Bomba de agua', watts: 750, zona: 'Agua', dedicado: true, fp: 0.85, motor: true, sinVerificar: true },
  { id: 'hidro', nombre: 'Hidroneumático', watts: 1100, zona: 'Agua', dedicado: true, fp: 0.85, motor: true, sinVerificar: true },

  // --- Resto ---
  { id: 'tv', nombre: 'Televisión', watts: 150, zona: 'Sala', sinVerificar: true },
  { id: 'compu', nombre: 'Computadora de escritorio', watts: 300, zona: 'Estudio', sinVerificar: true },
  { id: 'auto', nombre: 'Cargador de auto eléctrico', watts: 7400, zona: 'Cochera', dedicado: true, v: 220, sinVerificar: true },
  { id: 'alberca', nombre: 'Bomba de alberca', watts: 1100, zona: 'Exterior', dedicado: true, fp: 0.85, motor: true, sinVerificar: true }
]

export const aparatoPorId = id => APARATOS.find(a => a.id === id)

/**
 * Art. 430-22 — **el conductor que alimenta un motor se dimensiona al 125%** de su
 * corriente a plena carga.
 *
 * `FUENTE:` nota del documento técnico de Latincasa citando el artículo: *«Para
 * conductores que alimenten un solo motor, la corriente nominal a plena carga se
 * multiplicará por 1,25»*.
 *
 * La razón física: un motor arranca tirando varias veces su corriente nominal, y
 * trabaja de forma continua. Dimensionar un circuito de bomba a su corriente nominal
 * es exactamente cómo se quema un cable que «según la cuenta» estaba bien.
 *
 * **Se aplica solo a lo marcado `motor: true`** — bombas e hidroneumáticos. Los
 * minisplits NO llevan este factor aquí: su placa publica una corriente mínima de
 * circuito que ya lo incluye, y **si el aparato está a la vista, esa placa manda sobre
 * cualquier tabla de este archivo.**
 */
export const FACTOR_MOTOR = 1.25

/** Corriente mínima con que se dimensiona un circuito de alumbrado, en A. Criterio de
    holgura propio, no de norma: deja margen para lo que se agregue después. */
export const MINIMO_ALUMBRADO = 12

/**
 * Carga de alumbrado general por unidad de vivienda: **33 VA por metro cuadrado**.
 *
 * `FUENTE:` **texto primario**, Tabla 220-12 «Cargas de alumbrado general por tipo del
 * inmueble» de la NOM-001-SEDE-2012, publicada por el DOF el 29/11/2012, primera sección
 * (`dof.gob.mx/nota_detalle.php?codigo=5280607`). Renglón «Unidades de vivienda»: 33.
 *
 * ✅ **Confirmado el 2026-08-14.** Antes estaba marcado para contrastar porque el artículo
 * 220 no se había localizado en el DOF. Sí está: en la PRIMERA sección, la misma que trae
 * el índice y los artículos 100 y 110. Lo que había fallado era la extracción, no la
 * búsqueda — la página pesa 1.7 MB y el artículo 220 queda pasada la mitad. Se resolvió
 * bajándola con `curl --ssl-no-revoke` (el certificado del DOF tumba a curl por defecto)
 * y buscando en el HTML crudo.
 *
 * El mismo artículo aclara qué superficie cuenta: **se mide por fuera del edificio y se
 * excluyen patios abiertos, cocheras y espacios sin terminar** que no sean adaptables.
 */
export const VA_ALUMBRADO_M2 = 33

/**
 * La norma advierte de su propia tabla, en nota al pie de 220-12, y conviene repetirlo
 * donde se usa: *los valores unitarios se basan en condiciones de carga mínima y factor
 * de potencia de 100%, y puede ser que no provean capacidad suficiente*.
 *
 * O sea: 33 VA/m² es un **piso legal**, no una predicción de consumo. Un estudio con
 * cuatro monitores o una cocina con todo eléctrico se pasa, y por eso los aparatos se
 * cuentan aparte en vez de confiar en la superficie.
 */
export const AVISO_220_12 =
  'La carga por metro cuadrado es el mínimo que exige la norma, no una medición. Los ' +
  'aparatos se cuentan aparte justamente porque la superficie sola se queda corta.'

/**
 * Circuitos derivados mínimos exigidos, además de los de alumbrado.
 *
 * `FUENTE:` Art. 210-11(C) de la NOM-001-SEDE-2012, citado por electrica.mx:
 * dos o más circuitos de 20 A para aparatos pequeños, al menos uno de 20 A para la
 * lavadora, y al menos uno de 20 A para baños.
 *
 * Esto es exactamente el tipo de regla que un vendedor no conoce y que, omitida,
 * produce una cotización que se queda corta cuando llega la cuadrilla.
 */
export const CIRCUITOS_MINIMOS = [
  { id: 'peq-1', nombre: 'Aparatos pequeños de cocina', amperes: 20, cantidad: 2,
    fuente: 'Art. 210-11(C)' },
  { id: 'lav', nombre: 'Contactos de lavandería', amperes: 20, cantidad: 1,
    fuente: 'Art. 210-11(C)' },
  { id: 'bano', nombre: 'Contactos de baño', amperes: 20, cantidad: 1,
    fuente: 'Art. 210-11(C)' }
]

/**
 * TABLA 220-42 — factores de demanda de cargas de alumbrado.
 *
 * `FUENTE:` **texto primario**, transcrita del DOF del 29/11/2012, primera sección
 * (`codigo=5280607`). Renglón «Unidades de vivienda»:
 *
 * | Parte de la carga (VA) | Factor |
 * |---|---|
 * | Primeros 3000 o menos | 100% |
 * | De 3001 a 120 000     |  35% |
 * | A partir de 120 000   |  25% |
 *
 * ✅ **Confirmado el 2026-08-14**, y los tramos que ya estaban cargados resultaron
 * correctos. Se leyó el texto oficial de todas formas: acertar por costumbre y acertar
 * por lectura se ven idénticos hasta el día que no coinciden.
 *
 * `220-42` acota además dónde aplica, y la acotación importa: *«Esos factores no se deben
 * aplicar para calcular el número de circuitos derivados para iluminación general.»* Es
 * decir, el factor **reduce la acometida, no los circuitos**. El motor ya lo hacía bien
 * —los circuitos de alumbrado salen de la carga sin reducir— y ahora queda por escrito
 * de dónde sale esa distinción.
 *
 * Nota de transcripción: el renglón de hoteles del DOF dice literalmente «A partir de
 * 1 00000», con un espacio de más. Son 100 000; no afecta a vivienda, pero se registra
 * porque el error está en el texto oficial y quien vuelva a leerlo lo va a ver.
 */
export const DEMANDA_ALUMBRADO = {
  fuente: 'Tabla 220-42, NOM-001-SEDE-2012 (DOF 29/11/2012, 1a sección)',
  tramos: [
    { hasta: 3000, factor: 1.00 },
    { hasta: 120000, factor: 0.35 },
    { hasta: Infinity, factor: 0.25 }
  ]
}

export function demandaAlumbrado (va) {
  let resto = va, acumulado = 0, previo = 0
  for (const t of DEMANDA_ALUMBRADO.tramos) {
    const enTramo = Math.min(resto, t.hasta - previo)
    if (enTramo <= 0) break
    acumulado += enTramo * t.factor
    resto -= enTramo
    previo = t.hasta
  }
  return acumulado
}

/**
 * Art. 220-53: en vivienda se permite aplicar **75%** a la placa de **cuatro o más
 * aparatos fijos** conectados al mismo alimentador — excluyendo estufa, secadora,
 * calefacción eléctrica y aire acondicionado, que llevan sus propias reglas.
 *
 * `FUENTE:` texto primario, misma sección del DOF.
 *
 * Con tres aparatos fijos no aplica. Es exactamente el tipo de regla que un operador no
 * técnico no puede conocer y que, omitida, hace proponer una acometida más grande —y más
 * cara— de la que la norma pide.
 */
export const DEMANDA_APARATOS_FIJOS = { desde: 4, factor: 0.75, fuente: 'Art. 220-53' }

/**
 * Art. 220-52: cada circuito de aparatos pequeños y el de lavandería cuentan **1500 VA**
 * en el alimentador, y se permite sumarlos a la carga de alumbrado para aplicarles el
 * factor de la Tabla 220-42.
 *
 * `FUENTE:` texto primario. Trae una excepción que en México pesa: **no aplica a
 * viviendas de 60 m² o menos.**
 */
export const VA_POR_CIRCUITO_APARATOS = 1500
export const M2_EXCEPCION_220_52 = 60

/**
 * MÉTODO OPCIONAL PARA VIVIENDA — art. 220-82.
 *
 * `FUENTE:` texto primario, DOF 29/11/2012, primera sección. La norma **permite** calcular
 * el alimentador y la acometida de una vivienda así, en lugar del método por partes:
 *
 *   · **100% de los primeros 10 kVA + 40% del resto** de las cargas generales, que son
 *     33 VA/m², los 1500 VA por circuito de aparatos pequeños y lavandería, y la placa de
 *     todo aparato fijo, estufa, secadora, calentador de agua y motor permanente.
 *   · **Más la mayor** de seis posibilidades de clima. Aquí se implementa la que aplica al
 *     mercado de Soham Home: 100% de la placa del aire acondicionado.
 *
 * Condición de uso, y el sistema la comprueba: la vivienda debe alimentarse por **un solo
 * juego de tres conductores a 120/240 o 220Y/127 V, con acometida de 100 A o más**.
 *
 * **Por qué importa comercialmente:** este método casi siempre da una acometida más chica
 * que el método por partes. Proponer de más no es prudencia, es una cotización que pierde
 * contra quien sí calculó — y la norma permite las dos.
 */
export const OPCIONAL_VIVIENDA = {
  primerTramo: 10000,
  factorResto: 0.40,
  amperesMinimos: 100,
  fuente: 'Art. 220-82, NOM-001-SEDE-2012'
}

export function demandaOpcionalVivienda ({ vaGenerales, vaClima = 0 }) {
  const primero = Math.min(vaGenerales, OPCIONAL_VIVIENDA.primerTramo)
  const resto = Math.max(0, vaGenerales - OPCIONAL_VIVIENDA.primerTramo)
  return primero + resto * OPCIONAL_VIVIENDA.factorResto + vaClima
}

/* ============================================================================
   3. BALANCEO ENTRE FASES — matemática exacta, sin tabla que confirmar
   ========================================================================== */

/**
 * Reparte los circuitos entre las dos fases de una acometida 127/220 V minimizando el
 * desequilibrio.
 *
 * Es el problema de partición de conjuntos. Se usa el algoritmo voraz de
 * **mayor-carga-primero**: se ordenan los circuitos de mayor a menor y cada uno va a la
 * fase que en ese momento lleve menos carga. Para el tamaño de una casa —de 10 a 25
 * circuitos— da soluciones prácticamente óptimas, y su peor caso está acotado por
 * teoría conocida en 7/6 del óptimo.
 *
 * **Por qué importa, dicho para el cliente:** una casa con todo cargado de un lado
 * desperdicia la mitad de su capacidad contratada, y el neutro carga la diferencia.
 * Repartirlo no cuesta material: cuesta ordenar el tablero bien una vez.
 *
 * Los circuitos de 220 V toman de las dos fases por definición y no entran al reparto.
 */
export function balancear (circuitos) {
  const monofasicos = circuitos.filter(c => (c.tension || 127) < 200)
  const bifasicos = circuitos.filter(c => (c.tension || 127) >= 200)

  const orden = [...monofasicos].sort((a, b) => b.corriente - a.corriente)
  const fases = { A: [], B: [] }
  let cargaA = 0, cargaB = 0

  for (const c of orden) {
    if (cargaA <= cargaB) { fases.A.push(c); cargaA += c.corriente }
    else { fases.B.push(c); cargaB += c.corriente }
  }

  // Un circuito de 220 V carga las dos fases por igual.
  for (const c of bifasicos) { cargaA += c.corriente; cargaB += c.corriente }

  const mayor = Math.max(cargaA, cargaB)
  const desequilibrio = mayor > 0 ? (Math.abs(cargaA - cargaB) / mayor) * 100 : 0

  return {
    fases, bifasicos,
    cargaA, cargaB,
    desequilibrio,
    // Criterio de calidad propio: por encima del 10% conviene reacomodar.
    aceptable: desequilibrio <= 10,
    porque: `Fase A ${cargaA.toFixed(1)} A, fase B ${cargaB.toFixed(1)} A. ` +
      `Desequilibrio ${desequilibrio.toFixed(1)}%.` +
      (bifasicos.length ? ` ${bifasicos.length} circuito(s) de 220 V cargan ambas fases.` : '')
  }
}

/* ============================================================================
   4. CANALIZACIÓN
   ========================================================================== */

/**
 * Porcentaje de la sección del tubo que pueden ocupar los conductores.
 *
 * `FUENTE:` Tabla 1 del Capítulo 10 de la NOM-001-SEDE-2012, reproducida en la tesis
 * del IPN «Diseño de la Instalación Eléctrica en una Casa Habitación Implementando la
 * NOM-001-SEDE-2012» (repositorio público `tesis.ipn.mx`), su tabla 2.9.
 *
 * **Corrección del 2026-08-14.** La primera versión asumía 40% para todo y lo marcaba
 * como sin verificar. El 40% es correcto para tres o más, pero **para uno o dos
 * conductores los porcentajes son otros**, y son mayores.
 */
export const RELLENO = { uno: 0.53, dos: 0.31, masDeDos: 0.40 }
export const rellenoPara = n => (n === 1 ? RELLENO.uno : n === 2 ? RELLENO.dos : RELLENO.masDeDos)

/**
 * ÁREA REAL DEL CONDUCTOR CON SU AISLAMIENTO, en mm².
 *
 * `FUENTE:` Tabla 5 del Capítulo 10 de la NOM-001-SEDE-2012, reproducida en la misma
 * tesis (su tabla 2.10). Se leyeron directamente los calibres 14 a 3.
 *
 * **Este dato corrigió un error material.** La versión anterior estimaba el área
 * multiplicando la sección de cobre por 2.4. El factor real para THW es cercano a 7:
 * un 12 AWG tiene 3.31 mm² de cobre y **22.77 mm² con aislamiento**. La estimación se
 * quedaba corta por casi tres veces, y eso subdimensiona la canalización — que es un
 * error que se descubre en obra, metiendo cable a la fuerza en un tubo que no da.
 *
 * Se distinguen las dos familias porque **cambian el tubo por más del doble**:
 *   · `thw`  — aislamiento tradicional, el más usado en obra residencial mexicana.
 *   · `thhn` — aislamiento delgado (THHN/THWN); mismo cobre, mucho menos volumen.
 *
 * `CONFIRMAR:` de 2 AWG hacia arriba los valores no se leyeron del documento y se
 * marcan; para vivienda unifamiliar el rango leído cubre todos los circuitos derivados.
 */
export const AREA_AISLADO = {
  thw:  { '14': 18.90, '12': 22.77, '10': 28.19, '8': 53.87, '6': 67.16, '4': 86.00, '3': 98.13 },
  thhn: { '14': 6.258, '12': 8.581, '10': 13.61, '8': 23.61, '6': 32.71, '4': 53.16 }
}

/**
 * Dimensiones del tubo conduit y área disponible por factor de relleno.
 *
 * `FUENTE:` la misma tesis, tabla 2.12, «basado en la tabla 1 de este capítulo» de la
 * NOM-001-SEDE-2012, para tubo conduit no metálico.
 *
 * **Corrección del 2026-08-14.** Los valores anteriores estaban inflados: se tenía
 * 196 mm² para el tubo de ½" cuando su área total es **158 mm²**. Combinado con el
 * error del área del conductor, la canalización salía subdimensionada.
 */
export const TUBOS = [
  { metrica: 16, pulgadas: '1/2"',   interiorMm: 14.2, total: 158,  uno: 84,   dos: 49,  masDeDos: 63 },
  { metrica: 21, pulgadas: '3/4"',   interiorMm: 19.3, total: 293,  uno: 155,  dos: 91,  masDeDos: 117 },
  { metrica: 27, pulgadas: '1"',     interiorMm: 25.4, total: 507,  uno: 269,  dos: 157, masDeDos: 203 },
  { metrica: 35, pulgadas: '1 1/4"', interiorMm: 34.0, total: 908,  uno: 481,  dos: 281, masDeDos: 363 },
  { metrica: 41, pulgadas: '1 1/2"', interiorMm: 39.9, total: 1250, uno: 663,  dos: 388, masDeDos: 500 },
  { metrica: 53, pulgadas: '2"',     interiorMm: 51.3, total: 2067, uno: 1095, dos: 641, masDeDos: 827 }
]

/**
 * Elige el tubo para un grupo de conductores.
 *
 * Usa las columnas ya calculadas de la tabla —no multiplica el área total por el
 * porcentaje—, porque así lo hace el procedimiento de la norma y así se puede auditar
 * renglón contra renglón.
 */
export function tuboPara (conductores, aislamiento = 'thw') {
  const tabla = AREA_AISLADO[aislamiento] || AREA_AISLADO.thw
  const n = conductores.length
  const area = conductores.reduce((s, c) => s + (tabla[c.awg] ?? 0), 0)
  const columna = n === 1 ? 'uno' : n === 2 ? 'dos' : 'masDeDos'

  const t = TUBOS.find(t => t[columna] >= area)
  const elegido = t || TUBOS[TUBOS.length - 1]

  return {
    ...elegido,
    aislamiento,
    conductores: n,
    areaOcupada: area,
    disponible: elegido[columna],
    relleno: rellenoPara(n),
    ocupacion: area / elegido.total,
    insuficiente: !t,
    porque: `${n} conductor${n === 1 ? '' : 'es'} ${aislamiento.toUpperCase()} ocupan ` +
      `${area.toFixed(1)} mm². Al ${(rellenoPara(n) * 100).toFixed(0)}% de relleno, el tubo de ` +
      `${elegido.pulgadas} admite ${elegido[columna]} mm².`
  }
}

/* ============================================================================
   5. LA CADENA COMPLETA
   ========================================================================== */

/** Corriente de un aparato. `I = P / (V · fp)`. */
export const corrienteDe = ap => ap.watts / ((ap.v || 127) * (ap.fp || 1))

/**
 * De la casa levantada al sistema dimensionado.
 *
 * @param m2            metros cuadrados construidos
 * @param aparatos      [{ id, cantidad, metros }]  lo que el operador contó
 * @param ambienteC     temperatura ambiente de la canalización
 */
export function calcularInstalacion ({ m2, aparatos = [], ambienteC = 30, tension = 127,
                                       acometidaA = null, insumos = [], metrosPorDefecto = 20,
                                       aislamiento = 'thw', holguraObra = 1.10 }) {
  // --- Alumbrado general, por superficie ---
  const vaAlumbrado = m2 * VA_ALUMBRADO_M2
  const demandaLuz = demandaAlumbrado(vaAlumbrado)

  // --- Circuitos por aparato ---
  const circuitos = []
  let vaAparatos = 0

  for (const item of aparatos) {
    const ap = aparatoPorId(item.id)
    if (!ap) continue
    const n = item.cantidad || 1
    vaAparatos += ap.watts * n
    if (ap.dedicado) {
      for (let k = 0; k < n; k++) {
        const nominal = corrienteDe(ap)
        // Art. 430-22: el conductor de un motor se dimensiona al 125% de su corriente
        // a plena carga. El interruptor y el balanceo siguen usando la nominal.
        const corriente = ap.motor ? nominal * FACTOR_MOTOR : nominal
        const metros = item.metros || metrosPorDefecto
        const supuesto = !item.metros
        const calibre = calibrePara({ corriente, metros, tension: ap.v || tension, ambienteC })
        circuitos.push({
          nombre: ap.nombre + (n > 1 ? ` ${k + 1}` : ''),
          corriente: nominal,
          corrienteDimensionado: corriente,
          esMotor: !!ap.motor,
          metros, metrosSupuestos: supuesto, tension: ap.v || tension,
          dedicado: true, calibre
        })
      }
    }
  }

  /* --- Circuitos de alumbrado ---
     La carga de luz se reparte en circuitos de 15 A cargados al 80%, que es el criterio
     para carga continua —la iluminación de una casa está encendida horas seguidas—.

     `MINIMO_ALUMBRADO` existe porque dimensionar un circuito de alumbrado a la corriente
     que hoy le toca lo deja sin margen para el foco que se agregue mañana. Va como campo
     visible —`corrienteDimensionado`— y NO escondido dentro del cálculo: si la tabla
     muestra 9.7 A y el calibre se eligió con 12, hay que decirlo. Principio 9. */
  const corrienteLuz = demandaLuz / tension
  const nLuz = Math.max(1, Math.ceil(corrienteLuz / (15 * 0.8)))
  for (let k = 0; k < nLuz; k++) {
    const corriente = corrienteLuz / nLuz
    const dimensionado = Math.max(corriente, MINIMO_ALUMBRADO)
    const calibre = calibrePara({ corriente: dimensionado, metros: 25, tension, ambienteC })
    circuitos.push({
      nombre: `Alumbrado ${k + 1}`,
      corriente,
      corrienteDimensionado: dimensionado,
      razonDimensionado: dimensionado > corriente
        ? `Se dimensiona a ${MINIMO_ALUMBRADO} A y no a ${corriente.toFixed(1)} A, para dejar ` +
          'margen a lo que se agregue después.'
        : null,
      metros: 25, metrosSupuestos: true, tension, calibre
    })
  }

  // --- Circuitos mínimos exigidos por norma ---
  for (const min of CIRCUITOS_MINIMOS) {
    for (let k = 0; k < min.cantidad; k++) {
      const corriente = min.amperes * 0.8
      const calibre = calibrePara({ corriente, metros: metrosPorDefecto, tension, ambienteC })
      circuitos.push({
        nombre: min.nombre + (min.cantidad > 1 ? ` ${k + 1}` : ''),
        corriente, metros: metrosPorDefecto, metrosSupuestos: true, tension, exigido: min.fuente, calibre
      })
    }
  }

  const balance = balancear(circuitos)

  /* --- La acometida, por los DOS caminos que la norma permite ---

     No se elige uno por gusto. El método por partes (Parte C del art. 220) y el opcional
     de vivienda (220-82) son ambos válidos, dan números distintos, y el segundo casi
     siempre da menos. Se calculan los dos y se propone el menor de los aplicables: cobrar
     una acometida de más no es prudencia, es perder contra quien sí calculó.

     Y como los dos quedan a la vista, la decisión es auditable (Principio 9). */

  // Clima aparte: en el método opcional entra sin reducir, no como carga general.
  const idsClima = new Set(APARATOS.filter(a => a.zona === 'Clima').map(a => a.id))
  let vaClima = 0, vaAparatosGenerales = 0, nFijos = 0
  for (const item of aparatos) {
    const ap = aparatoPorId(item.id)
    if (!ap) continue
    const va = ap.watts * (item.cantidad || 1)
    if (idsClima.has(ap.id)) vaClima += va
    else { vaAparatosGenerales += va; if (ap.dedicado) nFijos += (item.cantidad || 1) }
  }

  // Los 1500 VA por circuito de aparatos pequeños y lavandería (220-52). No aplican a
  // vivienda de 60 m² o menos: la excepción es del texto de la norma, no un criterio.
  const circuitosPequenos = m2 > M2_EXCEPCION_220_52
    ? CIRCUITOS_MINIMOS.filter(c => c.id !== 'bano').reduce((s, c) => s + c.cantidad, 0)
    : 0
  const vaCircuitosPequenos = circuitosPequenos * VA_POR_CIRCUITO_APARATOS

  // Método por partes: 220-42 sobre alumbrado + circuitos pequeños, y 220-53 sobre los
  // aparatos fijos si son cuatro o más.
  const factorFijos = nFijos >= DEMANDA_APARATOS_FIJOS.desde ? DEMANDA_APARATOS_FIJOS.factor : 1
  const porPartes = demandaAlumbrado(vaAlumbrado + vaCircuitosPequenos)
                  + vaAparatosGenerales * factorFijos + vaClima

  // Método opcional 220-82.
  const vaGenerales = vaAlumbrado + vaCircuitosPequenos + vaAparatosGenerales
  const opcional = demandaOpcionalVivienda({ vaGenerales, vaClima })

  /* CONDICIÓN DE USO DEL MÉTODO OPCIONAL, y por qué se comprueba así.

     220-82(a) lo permite cuando la vivienda se alimenta «por un solo conjunto de tres
     conductores a 120/240 o 220Y/127 volts ... con una ampacidad de 100 amperes o más».
     Eso habla de **la acometida instalada**, no del resultado del cálculo. Es un dato de
     campo: el operador lo lee del interruptor principal en el primer paso de la guía.

     Un intento anterior lo comparaba contra la propia demanda calculada. Estaba mal por
     dos razones: confundía el insumo con el resultado, y producía una contradicción
     visible —la pantalla decía que mandaba el método por partes mientras el número
     mostrado era el del opcional—.

     Si nadie capturó la acometida, no se adivina: el opcional queda como «no se sabe» y
     manda el método por partes, que es el conservador. */
  const opcionalAplicable = acometidaA == null ? null
    : acometidaA >= OPCIONAL_VIVIENDA.amperesMinimos

  const usaOpcional = opcionalAplicable === true && opcional < porPartes
  const demandaTotalVA = usaOpcional ? opcional : porPartes
  const corrienteAcometida = demandaTotalVA / 220     // acometida bifásica 127/220

  return {
    m2,
    vaAlumbrado, demandaLuz, vaAparatos,
    vaClima, vaCircuitosPequenos, nAparatosFijos: nFijos,
    demanda: {
      porPartes,
      opcional,
      opcionalAplicable,
      manda: usaOpcional ? 'opcional' : 'porPartes',
      porque: usaOpcional
        ? `El método opcional del art. 220-82 da ${Math.round(opcional)} VA contra los ` +
          `${Math.round(porPartes)} VA del método por partes, y la norma permite los dos. ` +
          'Se propone el menor.'
        : opcionalAplicable === null
          ? `No se capturó el interruptor principal, así que no se sabe si el método ` +
            `opcional aplica —exige acometida de ${OPCIONAL_VIVIENDA.amperesMinimos} A o ` +
            'más—. Se usa el método por partes, que es el conservador. Si la acometida ' +
            `resulta de ${OPCIONAL_VIVIENDA.amperesMinimos} A o más, la demanda baja a ` +
            `${Math.round(opcional)} VA.`
          : opcionalAplicable === false
            ? `La acometida instalada es de ${acometidaA} A, y el método opcional exige ` +
              `${OPCIONAL_VIVIENDA.amperesMinimos} A o más. Manda el método por partes.`
            : 'El método por partes da menos en este caso, así que es el que se propone.',
      factorAparatosFijos: factorFijos,
      porqueFactorFijos: nFijos >= DEMANDA_APARATOS_FIJOS.desde
        ? `Son ${nFijos} aparatos fijos, así que el art. 220-53 permite el 75%.`
        : `Son ${nFijos} aparatos fijos. El 75% del art. 220-53 pide cuatro o más, así que ` +
          'aquí van al 100%.'
    },
    demandaTotalVA,
    corrienteAcometida,
    circuitos,
    balance,
    espaciosTablero: circuitos.length + Math.ceil(circuitos.length * 0.25),  // 25% de reserva
    /* Esta lista se vació casi por completo el 2026-08-14. Salieron: el factor de relleno
       y las áreas del conductor (Tabla 1 y Tabla 5 del capítulo 10), la carga por metro
       cuadrado (Tabla 220-12) y los factores de demanda (Tabla 220-42, art. 220-53 y
       220-82) — todos leídos del texto primario del DOF.

       Lo que queda es lo único que ninguna norma publica: cuánto consume el aparato que
       está instalado en ESA casa. Eso se lee de su placa, y es trabajo de campo. */
    sinCalibrar: ['potencias típicas de los aparatos, mientras no se lea su placa'],
    materiales: materialesElectrico({ circuitos, insumos, aislamiento, holguraObra }),

    /* CUÁNTO DE ESTE PRESUPUESTO DESCANSA EN UN SUPUESTO.
       La canalización es el renglón más caro de un recableado —abrir ranura, meter tubo y
       resanar—, y sale de los metros de cada circuito. Si nadie midió esas distancias, el
       número que domina el precio no es una medición: es un valor por defecto.
       Se cuenta y se dice. Un presupuesto que no distingue medido de supuesto se lee como
       si todo estuviera medido, y ése es el peor lugar donde puede pasar. */
    metros: (() => {
      const supuestos = circuitos.filter(c => c.metrosSupuestos)
      const total = circuitos.reduce((s, c) => s + c.metros, 0)
      const enSupuesto = supuestos.reduce((s, c) => s + c.metros, 0)
      return {
        total, enSupuesto,
        circuitosSupuestos: supuestos.length,
        porcentajeSupuesto: total ? enSupuesto / total : 0,
        porDefecto: metrosPorDefecto,
        porque: supuestos.length
          ? `${supuestos.length} de ${circuitos.length} circuitos van con los ${metrosPorDefecto} m ` +
            'que se suponen por defecto, no con una distancia medida. La canalización es lo ' +
            'más caro de la obra, así que medirlas es lo que más mueve este presupuesto.'
          : 'Todas las distancias están medidas.'
      }
    })()
  }
}

/**
 * Lista de material eléctrico, **cobrada con el catálogo de Juan**.
 *
 * Antes no existía: el motor calculaba calibres impecables y esa cadena no llegaba a
 * ningún precio. Es la costura que pidió Juan el 2026-08-14.
 *
 * Tres cosas que este cálculo hace y que un vendedor no haría de cabeza:
 *
 * 1. **Cuenta tres conductores por circuito, no uno.** Fase, neutro y tierra. Los de
 *    220 V llevan dos fases más tierra, así que también son tres. La tierra puede ir en
 *    calibre menor según 250-122, pero aquí se cuenta al mismo calibre: es lo que se
 *    compra en la práctica y evita una cotización que se queda corta en obra.
 * 2. **Agrupa el metraje por calibre**, no por circuito. El cable se compra por rollo de
 *    un calibre, no por circuito.
 * 3. **El aislamiento cambia el tubo.** No es solo precio: un 12 AWG THW ocupa 22.77 mm²
 *    y en THHN ocupa 8.581. Por eso `aislamiento` viaja hasta `tuboPara`.
 */
export function materialesElectrico ({ circuitos, insumos = [], aislamiento = 'thw', holguraObra = 1.10 }) {
  const lista = []
  const pct = Math.round((holguraObra - 1) * 100)

  // --- Conductor, agrupado por calibre ---
  const metrosPorCalibre = {}
  for (const c of circuitos) {
    const awg = c.calibre.awg
    // Tres hilos por circuito: fase + neutro + tierra, o dos fases + tierra en 220 V.
    metrosPorCalibre[awg] = (metrosPorCalibre[awg] || 0) + c.metros * 3
  }
  for (const [awg, metros] of Object.entries(metrosPorCalibre)) {
    const n = circuitos.filter(c => c.calibre.awg === awg).length
    lista.push(renglon({
      insumo: resolver(insumos, { clase: 'cable', awg, aislamiento }),
      cantidad: Math.ceil(metros * holguraObra), unidad: 'm',
      de: `${n} circuito${n === 1 ? '' : 's'} de ${awg} AWG, a tres hilos cada uno ` +
          `(${Math.round(metros)} m) más ${pct}% de holgura de obra`
    }))
  }

  // --- Canalización: un tubo por circuito, dimensionado por sus propios conductores ---
  const metrosPorTubo = {}
  for (const c of circuitos) {
    const t = tuboPara([{ awg: c.calibre.awg }, { awg: c.calibre.awg }, { awg: c.calibre.awg }], aislamiento)
    metrosPorTubo[t.pulgadas] = (metrosPorTubo[t.pulgadas] || 0) + c.metros
  }
  for (const [pulgadas, metros] of Object.entries(metrosPorTubo)) {
    lista.push(renglon({
      insumo: resolver(insumos, { clase: 'conduit', pulgadas }),
      cantidad: Math.ceil(metros * holguraObra), unidad: 'm',
      de: `Tres conductores ${aislamiento.toUpperCase()} por circuito piden tubo de ` +
          `${pulgadas}. Son ${Math.round(metros)} m más ${pct}% de holgura`
    }))
  }

  // --- Protecciones: una por circuito, al paso comercial que Juan tenga ---
  const porProteccion = {}
  for (const c of circuitos) {
    const amp = proteccionPara(c.corrienteDimensionado || c.corriente, c.calibre.awg)
    const polos = (c.tension || 127) >= 220 ? 2 : 1
    const k = `${amp}|${polos}`
    porProteccion[k] = (porProteccion[k] || 0) + 1
  }
  for (const [k, cantidad] of Object.entries(porProteccion)) {
    const [amp, polos] = k.split('|').map(Number)
    lista.push(renglon({
      insumo: resolverProteccion(insumos, amp, polos),
      cantidad, unidad: 'pieza',
      de: `${cantidad} circuito${cantidad === 1 ? '' : 's'} de ${polos} polo${polos === 1 ? '' : 's'} ` +
          `protegido${cantidad === 1 ? '' : 's'} a ${amp} A (art. 240-4(d))`
    }))
  }

  return lista
}
